import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import type {IJSONProvider} from "data/providers/json/json.provider";
import {Bindings} from "data/constants/bindings";
import {type IRoundsStore} from "data/stores/rounds/rounds.store";

export interface ISquad {
	id: number;
	competitionId: number;
	name: string;
	abbreviation: string;
	darkBadge: string;
	lightBadge: string;
	jersey: string;
	locked: boolean;
	totalPoints: number;
	averagePoints: number;
	roundPoints: number | null;
	percentSelected: number;
	leaguePosition: number;
	last3Form: string[];
	backgroundColor: string;
	textColor: string;
}

export interface ISquadsStore {
	get getIsLoading(): boolean;
	get list(): ISquad[];
	getSquadById(squadId: number): ISquad | undefined;
	getSquadByAbbr(abbr: string): ISquad | undefined;
	getNextOpponent(squadId: number): ISquad | undefined;
	fetchSquads(): Promise<void>;
}

@injectable()
export class SquadsStore implements ISquadsStore {
	@observable private _isLoading: boolean = false;

	constructor(
		@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore
	) {
		makeAutoObservable(this);
	}

	@observable private _list: ISquad[] = [];

	get list() {
		return this._list;
	}

	get getIsLoading(): boolean {
		return this._isLoading;
	}

	getSquadById(squadId: number): ISquad | undefined {
		return this._list.find((squad) => squad.id === squadId);
	}

	getSquadByAbbr(abbr: string): ISquad | undefined {
		return this._list.find((squad) => squad.abbreviation === abbr);
	}

	getNextOpponent(squadId: number) {
		const nextFixture = this._roundsStore.getNextFixture(squadId);
		if (!nextFixture) {
			return;
		}

		const squadIdNext =
			nextFixture.homeId === squadId ? nextFixture.awayId : nextFixture.homeId;
		return this.getSquadById(squadIdNext);
	}

	@action
	async fetchSquads() {
		const {data} = await this._jsonProvider.squads();

		runInAction(() => {
			this._list = data;
		});
	}
}
